import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import {
  TextElement,
  TranscriptElement,
} from '../models/TranscriptElement';
import { TranscriptEditorChangeType } from '../types';
import { Paragraph } from '../models/Paragraph';
/** Information we need to restore each element on undo() */
interface ElementSpeakerInfo {
  segmentId: string;
  paragraphId: string;
  elementId: string;
  originalSpeakerLabel: string;
  newParagraphId?: string;
}

export interface SelectedTextElement {
  segmentId: string;
  paragraphId: string;
  element: TextElement;
}

interface ElementGroups {
  [segmentId: string]: {
    [paragraphId: string]: SelectedTextElement[];
  };
}

/**
 * Re‑labels an arbitrary selection of text with a new speaker.
 * Ensures head / middle / tail segments have accurate timing and order.
 */
export class AssignSpeakerToSelectedTextCommand extends AbstractTranscriptEditorCommand {
  private readonly selected: SelectedTextElement[];
  private readonly explicitLabel?: string;
  private readonly datoId?: string;
  private readonly userId: string;

  private modified: ElementSpeakerInfo[] = [];

  constructor (
    editor: TranscriptEditorCore,
    selected: SelectedTextElement[],
    speakerLabel: string | undefined,
    datoSpeakerId: string | undefined,
    userId: string,
  ) {
    super(
      speakerLabel
        ? `Assign speaker ${speakerLabel.slice(0, 4)}…`
        : datoSpeakerId
          ? `Assign speaker (Dato ${datoSpeakerId.slice(0, 4)})…`
          : 'Assign speaker to selection',
      editor,
    );

    this.selected      = selected;
    this.explicitLabel = speakerLabel;
    this.datoId        = datoSpeakerId;
    this.userId        = userId;
    
    // Set this command to require sorting after execution
    this.setRequiresSorting(true);
    this.setNeedsHeavyOptimization(true);
  }

  // ──────────────────────────────────────────────────────────── execute()
  async execute(): Promise<boolean> {
    try {
      if (!this.selected.length) return false;
      const label = this.resolveLabel();
      if (!label) return false;
      
      // Store the current state for undo
      const grouped = this.groupBySegmentAndParagraph(this.selected);
      
      // Capture original state for potential undo
      const originalSelectedElements = [...this.selected];
      this.modified = this.captureOriginalState(originalSelectedElements);
      this.editor.beginBatch();
      try {
        for (const segId of Object.keys(grouped)) {
          const segment = this.editor.getSegment(segId);
          if (!segment) continue;
          const SegmentClass = this.editor.getSegmentClass();
          const container = this.editor.getContainer();

          const { head, middle, tail } = this.splitSegment(segment, grouped[segId], label);
          
          const headSegment = this.buildSegment(SegmentClass, segment.speakerLabel, head);
          if (headSegment) {
            container?.addSegment(headSegment);
          }
          if (middle.length) {
            const middleSegment = this.buildSegment(SegmentClass, label, middle);
            if (middleSegment) {
              container?.addSegment(middleSegment);
            }
          }
          if (tail.length) {
            const tailSegment = this.buildSegment(SegmentClass, segment.speakerLabel, tail);
            if (tailSegment) {
              container?.addSegment(tailSegment);
            }
          }
          container?.removeSegment(segId)
        }
        if (this.datoId) this.editor.updateSpeakerMapping(label, this.datoId);

        // Notify observers about the changes with immediate delivery
        this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
          container: this.editor.getContainer(),
          _forceImmediate: true
        });
        
        this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
          speakerLabel: label,
          affectedElements: this.selected.length,
          _forceImmediate: true
        });
        
        // Record the operation for persistence
        this.editor.recordOperation({
          type: 'speaker_assignment',
          data: {
            selectedElementIds: this.selected.map(s => s.element.id),
            newSpeakerLabel: label,
            datoSpeakerId: this.datoId || '',
            scope: 'selection'
          },
          userId: this.userId,
          timestamp: Date.now()
        });

        return true;
      } catch (err) {
        return false;
      } finally {
        this.editor.endBatch();
      }
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Captures the original state of the selected elements for undo operations
   */
  private captureOriginalState(selectedElements: SelectedTextElement[]): ElementSpeakerInfo[] {
    return selectedElements.map(sel => ({
      segmentId: sel.segmentId,
      paragraphId: sel.paragraphId,
      elementId: sel.element.id,
      originalSpeakerLabel: sel.element.speakerLabel || ''
    }));
  }

  // ──────────────────────────────────────────────────────────── undo()
  async undo(): Promise<boolean> {
    if (!this.modified.length) return true;

    this.editor.beginBatch();
    try {
      for (const info of this.modified) {
        // if segment or paragraph is not found rebuild it, and then remove empty segments and paragraphs
        const segment = this.editor.getSegment(info.segmentId);
        if (segment) {
          this.editor.assignElementSpeaker(
            info.segmentId,
            info.newParagraphId ?? info.paragraphId,
            info.elementId,
            info.originalSpeakerLabel,
            '',
            this.userId,
          );
        }
      }
      
      
      // Notify observers about the restored state
      this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
        container: this.editor.getContainer(),
        _forceImmediate: true
      });
      
      return true;
    } catch (err) {
      return false;
    } finally {
      this.editor.endBatch();
    }
  }

  // ──────────────────────────────────────────────────────────── helpers
  private resolveLabel(): string | undefined {
    if (this.explicitLabel) return this.explicitLabel;
    if (!this.datoId) return undefined;
    return this.findSpeakerLabelForDatoId(this.datoId) ?? `spk_${this.editor.nextSpeakerNumber()}`;
  }

  private groupBySegmentAndParagraph(list: SelectedTextElement[]): ElementGroups {
    const out: ElementGroups = {};
    for (const sel of list) {
      out[sel.segmentId] ||= {};
      out[sel.segmentId][sel.paragraphId] ||= [];
      out[sel.segmentId][sel.paragraphId].push(sel);
    }

    for (const [segId, paras] of Object.entries(out)) {
      const segment = this.editor.getSegment(segId);
      if (!segment) continue;
      for (const [paraId, arr] of Object.entries(paras)) {
        const paragraph = segment.getParagraph(paraId);
        if (!paragraph) continue;
        const idx = new Map<string, number>();
        paragraph.elements.forEach((el, i) => idx.set(el.id, i));
        arr.sort((a, b) => (idx.get(a.element.id) ?? 1e6) - (idx.get(b.element.id) ?? 1e6));
      }
    }
    return out;
  }

  private splitSegment(segment: any, selByPara: { [id: string]: SelectedTextElement[] }, newLabel: string) {
    const ParagraphClass = this.editor.getParagraphClass();

    const head: Paragraph[]   = [];
    const middle: Paragraph[] = [];
    const tail: Paragraph[]   = [];

    let seenSelection = false;

    for (const para of segment.paragraphs) {
      const selections = selByPara[para.id];
      if (!selections) {
        (seenSelection ? tail : head).push(para);
        continue;
      }

      seenSelection = true;

      // index map for quick lookup
      const idx = new Map<string, number>();
      para.elements.forEach((el: TranscriptElement, i: number) => idx.set(el.id, i));
      const first = Math.min(...selections.map((s: SelectedTextElement) => idx.get(s.element.id)!));
      const last  = Math.max(...selections.map((s: SelectedTextElement) => idx.get(s.element.id)!));

      const pre  = para.elements.slice(0, first);
      const sel  = para.elements.slice(first, last + 1);
      const post = para.elements.slice(last + 1);

      // Make sure we don't create paragraphs that only contain punctuation
      const preContainsText = pre.some((el: TranscriptElement) => el.type === 'text');
      const selContainsText = sel.some((el: TranscriptElement) => el.type === 'text');
      const postContainsText = post.some((el: TranscriptElement) => el.type === 'text');
      
      // Only create paragraphs if they have text elements or are attached to text-containing paragraphs
      if (pre.length && (preContainsText || (!preContainsText && !selContainsText && !postContainsText))) {
        head.push(this.makeParagraph(ParagraphClass, pre, para));
      }
      
      if (sel.length && selContainsText) {
        middle.push(this.makeParagraph(ParagraphClass, sel, para, newLabel));
      }
      
      if (post.length && (postContainsText || (!preContainsText && !selContainsText && !postContainsText))) {
        tail.push(this.makeParagraph(ParagraphClass, post, para));
      }
    }
    return { head, middle, tail };
  }

  private makeParagraph(Cls: any, elements: TranscriptElement[], base: any, overrideLabel?: string): Paragraph {
    const els = elements.map(el => {
      if (overrideLabel && el.type === 'text') (el as TextElement).speakerLabel = overrideLabel;
      return el;
    });

    // Find all text elements with valid timing
    const textElements = els.filter(el => el.type === 'text') as TextElement[];
    const timedElements = textElements.filter(
      el => el.startTime !== undefined && el.endTime !== undefined
    );
    
    // Calculate precise timing from text elements
    let start = Infinity, end = 0;
    
    if (timedElements.length > 0) {
      // Use the earliest start time and latest end time from timed elements
      start = Math.min(...timedElements.map((el: TextElement) => el.startTime || Infinity));
      end = Math.max(...timedElements.map((el: TextElement) => el.endTime || 0));
    } else if (textElements.length > 0) {
      // If no timed elements but text elements exist, use base timing and distribute evenly
      start = base.startTime;
      end = base.endTime;
      
      // Distribute timing evenly across text elements if they're missing timing
      const duration = end - start;
      const segmentPerElement = duration / textElements.length;
      
      textElements.forEach((el, index) => {
        el.startTime = start + (index * segmentPerElement);
        el.endTime = start + ((index + 1) * segmentPerElement);
      });
    } else {
      // If no text elements at all, use base timing
      start = base.startTime;
      end = base.endTime;
    }

    const id = this.uid('para1');
    return new Cls(id, start, end, els, base.segmentId) as Paragraph;
  }

  private buildSegment(SegmentCls: any, speaker: string, paragraphs: Paragraph[]) {
    // Filter out empty paragraphs or paragraphs with only punctuation
    const validParagraphs = paragraphs.filter((para: Paragraph) => {
      const hasTextElements = para.elements.some((el: TranscriptElement) => el.type === 'text');
      const hasValidTiming = para.startTime !== undefined && para.endTime !== undefined;
      return hasTextElements && hasValidTiming;
    });
    
    if (validParagraphs.length === 0) {
      return null; // Return null for empty segments to be filtered out later
    }
    
    // Calculate segment start/end time from paragraphs
    const startTime = Math.min(...validParagraphs.map((p: Paragraph) => p.startTime || Infinity));
    const endTime = Math.max(...validParagraphs.map((p: Paragraph) => p.endTime || 0));
    
    // Create segment with pre-calculated timing to ensure correct ordering
    const seg = new SegmentCls(this.uid('seg1'), speaker, startTime, endTime, validParagraphs, []);
    
    seg.updateInternalData();
    // Double-check timing with editor method
    this.editor.updateSegmentTiming(seg);
    return seg;
  }

  private findSpeakerLabelForDatoId(id: string) {
    const map = this.editor.getSpeakerMappings()?.mappings;
    return map ? Object.entries(map).find(([, dato]) => dato === id)?.[0] : undefined;
  }

  private uid(prefix: string) {
    return typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? `${prefix}_${crypto.randomUUID()}`
      : `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
}
